import { formatUsd } from "../../lib/pricing";
import { wasSubstituted, type TurnLogEntry } from "./turnLog";

/**
 * What each model actually did: which one answered, how long it took, how many
 * tokens it burned and what that cost.
 *
 * Every figure here comes from the worker's report of the completed turn, not from
 * what the client requested — the two can differ, and a comparison that shows the
 * request is measuring the wrong thing.
 */
export function ActivitySidebar({
  entries,
  onClear,
}: {
  entries: TurnLogEntry[];
  onClear: () => void;
}) {
  const totalCost = entries.reduce((sum, e) => sum + (e.cost?.usd ?? 0), 0);
  const anyEstimated = entries.some((e) => e.cost?.source === "estimate");
  const anyUnpriced = entries.some((e) => e.cost === null);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {entries.length > 0 && (
        <div className="flex justify-end px-3 pt-2">
          <button onClick={onClear} className="text-xs text-maven-gray-500 dark:text-maven-gray-500 hover:text-maven-gray-800">
            Clear
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
        {entries.length === 0 ? (
          <p className="pt-4 text-center text-xs text-maven-gray-500 dark:text-maven-gray-500">
            Send a prompt to see which model answered, and what it cost.
          </p>
        ) : (
          entries.map((e) => (
            <div key={e.id} className="rounded-md border border-maven-gray-100 dark:border-maven-gray-800 bg-white dark:bg-maven-gray-800 p-2 text-xs">
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-medium text-maven-gray-800 dark:text-maven-gray-100 truncate" title={e.servedModel ?? e.requestedModel}>
                  {e.servedModel ?? e.requestedModel}
                </span>
                <span className="shrink-0 text-maven-gray-500 dark:text-maven-gray-500">{(e.elapsedMs / 1000).toFixed(1)}s</span>
              </div>

              {wasSubstituted(e) && (
                <div className="mt-1 text-[11px] text-maven-orange-700">
                  requested {e.requestedModel}
                </div>
              )}

              <div className="mt-1 flex items-baseline justify-between gap-2 text-maven-gray-500 dark:text-maven-gray-500">
                <span>
                  {e.inputTokens ?? "—"} in / {e.outputTokens ?? "—"} out
                </span>
                <span className="font-medium text-maven-gray-800 dark:text-maven-gray-100">
                  {e.cost ? formatUsd(e.cost.usd) : "—"}
                  {e.cost?.source === "estimate" && <span title="Estimated rate, not a published price">*</span>}
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {entries.length > 0 && (
        <div className="border-t border-maven-gray-100 dark:border-maven-gray-800 px-3 py-2 text-xs">
          <div className="flex items-baseline justify-between">
            <span className="text-maven-gray-500 dark:text-maven-gray-500">{entries.length} turn{entries.length === 1 ? "" : "s"}</span>
            <span className="font-medium text-maven-gray-800 dark:text-maven-gray-100">{formatUsd(totalCost)}</span>
          </div>
          {/* Say which numbers are soft rather than presenting a total as fact. */}
          {(anyEstimated || anyUnpriced) && (
            <p className="mt-1 text-[11px] leading-snug text-maven-gray-500 dark:text-maven-gray-500">
              {anyEstimated && "* GPT rates are an internal ceiling, not published prices. "}
              {anyUnpriced && "Some turns have no known rate and are excluded."}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
